import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Autocomplete, Box, DialogActions, DialogContent, TextField, OutlinedInput, MenuItem, Select } from "@mui/material";
import TextInputComponent from "../../../atom/Inputs/TextInput";
import TextButtonComponet from "../../../atom/Buttons/TextButton";
import { DatePickerComponent, TimePickerComponent } from "../../../atom";
import { useDispatch, connect } from 'react-redux';
import { Actions } from "../../../../core/modules/Actions";
import { convertStringDate, convertStringTime } from "../../../../core/Constant";
import dayjs from 'dayjs';
import customParseFormat from "dayjs/plugin/customParseFormat";
import WebinarPreviewModal from "../../WebinarPreviewModal";

dayjs.extend(customParseFormat);
const extractCourseIdsFromWebinar = (webinar) => {
  if (!webinar) return [];

  //valid positive number, or null
  const toValidId = (value) => {
    const id = Number(value);
    return Number.isFinite(id) && id > 0 ? id : null;
  };

  if (Array.isArray(webinar.courses) && webinar.courses.length > 0) {
    return webinar.courses
      .map((course) => toValidId(course?.id))
      .filter(Boolean);
  }

  if (Array.isArray(webinar.course_ids) && webinar.course_ids.length > 0) {
    return webinar.course_ids.map(toValidId).filter(Boolean);
  }

  const singleId = toValidId(webinar.course_id);
  return singleId ? [singleId] : [];
};

const initialErrors = {
  name: "",
        date: "",
        time: "",
        duration: "",
        course: "",
        join_url: "",
        timezone: "",
        course_ids: "",
        note: "",
      };

  const initialFormState = {
    name: "",
    date: "",
    time: "",
    time_ext: "AM",
    duration: "",
    course: "",
    join_url: "",
    timezone: "Asia/Kolkata",
    note: "",
  };

const AdminWebinarUpdateForm = ({ onClose, courseList, getCourseList, webinarDetails, open }) => {
    const dispatch = useDispatch();
    const courseSelectRef = useRef(null);
    const [courseMenuWidth, setCourseMenuWidth] = useState(null);

      // Preview Modal States
      const [isPreviewOpen, setIsPreviewOpen] = useState(false);
      const [stagedPayload, setStagedPayload] = useState(null);
    const [newWebinar, setNewWebinar] = useState(initialFormState);
      const [selectedCourseIds, setSelectedCourseIds] = useState([]);
      const [errors, setErrors] = useState(initialErrors);

    const activeCourseList = useMemo(() => {
        if (!Array.isArray(courseList)) return [];
        return courseList
            .map((c) => ({
                id: Number(c?.id ?? c?.course_id),
                name: c?.name ?? c?.course_name,
            }))
            .filter((c) => Number.isFinite(c.id) && Boolean(c.name));
    }, [courseList]);

    const courseNameById = useMemo(() => {
        const map = new Map();
        activeCourseList.forEach((c) => map.set(c.id, c.name));
        return map;
    }, [activeCourseList]);

    const timezoneOptions = useMemo(() => {
        try {
            const supported = Intl.supportedValuesOf?.("timeZone");
            if (Array.isArray(supported) && supported.length > 0) {
                const normalized = supported.map((timezone) =>
                    timezone === "Asia/Calcutta" ? "Asia/Kolkata" : timezone
                );
                const priority = ["Australia/Sydney", "Asia/Kolkata"];
                return [
                    ...priority,
                    ...normalized.filter((timezone) => !priority.includes(timezone)),
                ];
            }
        } catch (err) {
      console.error("Failed to get timezone options", err);
        }
        return [
            "Australia/Sydney",
            "Asia/Kolkata",
            "Asia/Dubai",
            "Asia/Singapore",
            "Asia/Tokyo",
            "Asia/Shanghai",
            "Europe/London",
            "Europe/Paris",
            "Europe/Berlin",
            "America/New_York",
            "America/Chicago",
            "America/Denver",
            "America/Los_Angeles",
        ];
        }, []);

    useEffect(() => {
      getCourseList();
    }, [getCourseList]);

    useEffect(() => {
    if (!open || !webinarDetails) return;
        setSelectedCourseIds(extractCourseIdsFromWebinar(webinarDetails));
        setNewWebinar({
            name: webinarDetails?.name || '',
            date: webinarDetails?.date ? new Date(webinarDetails.date) : '',
            time: webinarDetails?.time ? dayjs(
                                `${webinarDetails.time} ${webinarDetails.time_ext || "AM"}`,
                                "hh:mm:ss A"
                                )
                            : '',
            time_ext: webinarDetails?.time_ext || 'AM',
            duration: webinarDetails?.duration || '',
            course: webinarDetails?.course_id || '',
            join_url: webinarDetails?.join_url ?? '',
            timezone: webinarDetails?.timezone || 'Asia/Kolkata',
            note: webinarDetails?.note || '',
          });

          setErrors(initialErrors);
          setStagedPayload(null);
        }, [open, webinarDetails]);

  const emptyFormfields = useCallback(() => {
        setNewWebinar(initialFormState);
        setSelectedCourseIds([]);
        setErrors(initialErrors);
        setStagedPayload(null);
      }, []);

      const handleChange = useCallback((name, value) => {
        setNewWebinar((prev) => ({
          ...prev,
          [name]: value,
        }));
        setErrors((prev) => ({
          ...prev,
                    [name]: name === "name" && value.length > 55
                        ? "Title must be at most 55 characters"
                        : name === "note" && value.length > 100
                            ? "Note must be at most 100 characters"
                            : "",
        }));
    }, []);

    const handleManualCourseIdsChange = (event) => {
        const value = event.target.value;
        const ids = typeof value === "string" ? value.split(",") : value;
        setSelectedCourseIds(
            ids.map((v) => Number(v)).filter((v) => Number.isFinite(v))
        );
        setErrors((prev) => ({ ...prev, course_ids: "" }));
    };

    const validateForm = () => {
        const newErrors = { ...initialErrors };
        if (!newWebinar?.name) {
            newErrors.name = "Webinar title is required";
          } else if (newWebinar.name.length < 2) {
            newErrors.name = "Title must be at least 2 characters";
          } else if (newWebinar.name.length > 55) {
            newErrors.name = "Title must be at most 55 characters";
        }
                if ((newWebinar?.note || "").length > 100) {
                        newErrors.note = "Note must be at most 100 characters";
                }
        if (!newWebinar?.date) {
            newErrors.date = "Date is required";
        } else {
          const selectedDate = dayjs(newWebinar.date).startOf("day");
          const today = dayjs().startOf("day");

          if (selectedDate.isValid() && selectedDate.isBefore(today)) {
            newErrors.date = "Past dates are not allowed";
          }
        }

        if (!newWebinar?.time) {
            newErrors.time = "Time is required";
        }
        if (!newWebinar?.duration) {
            newErrors.duration = "Duration is required";
        }

            const joinUrlTrimmed = (newWebinar?.join_url || "").trim();
            if (!joinUrlTrimmed) {
                newErrors.join_url = "Meeting URL is required";
            } else {
                try {
                    const parsed = new URL(joinUrlTrimmed);
                    if (!/^https?:$/i.test(parsed.protocol)) {
                        newErrors.join_url = "URL must start with http:// or https://";
                    }
                } catch {
                    newErrors.join_url = "Enter a valid URL (include https:// or http://)";
                }
            }
            if (!(newWebinar.timezone || "").trim()) {
                newErrors.timezone = "Timezone is required";
            }
            if (!Array.isArray(selectedCourseIds) || selectedCourseIds.length === 0) {
                newErrors.course_ids = "Please select at least one course";
        }
        setErrors(newErrors);
        return Object.values(newErrors).every((error) => !error);
  }

    const handleOpenPreview = (event) => {
    if (event) event.preventDefault();
    if (!validateForm()) return;

    const convertedTime = convertStringTime(newWebinar.time);
            const courseIds = selectedCourseIds
                .map((courseId) => Number(courseId))
                .filter((courseId) => Number.isFinite(courseId) && courseId > 0);
            const payload = {
                id: webinarDetails?.id,
                name: newWebinar.name,
                date: newWebinar.date ? convertStringDate(newWebinar.date) : undefined,
                time: convertedTime?.convertTime?.time,
                time_ext: convertedTime?.convertTime?.timeExt || 'AM',
                duration: newWebinar.duration ? Number(newWebinar.duration) : undefined,
                join_url: (newWebinar.join_url || '').trim(),
                course_ids: courseIds,
                selectedCourseNames: courseIds.map((id) => courseNameById.get(id) || id),
                timezone: (newWebinar.timezone || '').trim(),
                note: newWebinar.note || '',
            };
    setStagedPayload(payload);
    setIsPreviewOpen(true);
  };

    const handleConfirmUpdate = () => {
        if (!stagedPayload) return;

            dispatch(Actions.webinar.updateWebinarAdmin(stagedPayload));
          setIsPreviewOpen(false);
            onClose();
    };
    return (
        <>
            <form onSubmit={handleOpenPreview}>
                <DialogContent>
                    <Box>
                        <Box
                            sx={{
                                mb: 1.5,
                                color: "#4E657C",
                                fontSize: 14,
                                fontWeight: 500,
                            }}
                        >
                            * Required fields
                        </Box>
                        <Box>
                            <TextInputComponent
                                label={"Webinar Title *"}
                                placeholder="Enter webinar title"
                                name="name"
                                value={newWebinar.name}
                                onchange={(e) => handleChange("name", e.target.value)}
                                readOnly={false}
                                isError={Boolean(errors?.name)}
                                error={errors?.name}
                                required
                            />
                        </Box>
                        
                                <Box sx={{ mt: 2 }}>
                                    <TextInputComponent
                                        label={"Meeting URL *"}
                                        placeholder="https://..."
                                        name="join_url"
                                        value={newWebinar.join_url}
                                        onchange={(e) => handleChange("join_url", e.target.value)}
                                        readOnly={false}
                                        isError={Boolean(errors?.join_url)}
                                        error={errors?.join_url}
                                        required
                                    />
                                </Box>

                                <Box sx={{ mt: 2 }}>
                                    <TextInputComponent
                                        label={"Webinar Note"}
                                        placeholder="Enter note"
                                        name="note"
                                        value={newWebinar.note}
                                        onchange={(e) => handleChange("note", e.target.value)}
                                        readOnly={false}
                                        isError={Boolean(errors?.note)}
                                        error={errors?.note}
                                        required
                                    />
                                </Box>
                                
                                <Box sx={{ mt: 2, px: 1.25 }}>
                                    <p
                                        style={{
                                            borderRadius: "10px",
                                            padding: 0,
                                            margin: 0,
                                            marginBottom: 10,
                                            color: "#4E657C",
                                            fontSize: 19,
                                            fontWeight: 700,
                                        }}
                                    >
                                        Select Courses *
                                    </p>
                                    <Select
                                        ref={courseSelectRef}
                                        multiple
                                        fullWidth
                                        autoWidth={false}
                                        onOpen={() => {
                                            setCourseMenuWidth(courseSelectRef.current?.clientWidth || null);
                                        }}
                                        value={selectedCourseIds}
                                        onChange={handleManualCourseIdsChange}
                                        input={<OutlinedInput placeholder="Select Courses" />}
                                        displayEmpty
                                        MenuProps={{
                                            disableScrollLock: true,
                                            disablePortal: true,
                                            marginThreshold: 0,
                                            anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                            transformOrigin: { vertical: "top", horizontal: "left" },
                                            PaperProps: {
                                                sx: {
                                                    width: courseMenuWidth || "100%",
                                                    minWidth: `${courseMenuWidth || 0}px !important`,
                                                    maxWidth: courseMenuWidth ? `${courseMenuWidth}px` : "100%",
                                                    maxHeight: 240,
                                                    overflowX: "hidden",
                                                    overflowY: "auto",
                                                },
                                            },
                                            MenuListProps: { sx: { width: "100%", boxSizing: "border-box" } },
                                        }}
                                        sx={{
                                            width: "100%",
                                            minWidth: 0,
                                            "& .MuiSelect-select": {
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            },
                                        }}
                                        renderValue={(selected) => {
                                            if (!Array.isArray(selected) || selected.length === 0) {
                                                return "Select Courses";
                                            }
                                            return selected
                                                .map((id) => courseNameById.get(Number(id)) || String(id))
                                                .join(", ");
                                        }}
                                    >
                                        {activeCourseList.map((course) => (
                                            <MenuItem
                                                key={course.id}
                                                value={course.id}
                                                sx={{ display: "block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                            >
                                                {course.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors?.course_ids && (
                                        <p className="input-error-text">{errors.course_ids}</p>
                                    )}
                                </Box> 
                                <Box
                                              sx={{
                                                display: "grid",
                                                gridTemplateColumns: {
                                                  xs: "minmax(0, 1fr)",
                                                  sm: "repeat(2, minmax(0, 1fr))",
                                                },
                                                gap: 1.5,
                                                alignItems: "start",
                                                minWidth: 0,
                                                mt: 2,
                                              }}
                                            >
                        <Box sx={{ minWidth: 0 }}>
                            <DatePickerComponent
                                label={"Date *"}
                                selected={newWebinar.date}
                                dateFormat="dd/MM/yyyy"
                                onChange={(e) => handleChange("date", e)}
                                isError={Boolean(errors?.date)}
                                error={errors?.date}
                                minDate={dayjs().startOf("day").toDate()}
                            />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                            <TimePickerComponent
                                label={"Time *"}
                                value={newWebinar.time}
                                onChange={(e) => handleChange('time', e)}
                                isError={Boolean(errors?.time)}
                                error={errors?.time}
                            />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                            <TextInputComponent
                                type="number"
                                label={"Duration (MIN) *"}
                                placeholder="Enter Webinar duration"
                                name="duration"
                                value={newWebinar.duration}
                                onchange={(e) => handleChange("duration", e.target.value)}
                                readOnly={false}
                                isError={Boolean(errors?.duration)}
                                error={errors?.duration}
                            />

                        </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <p
                                        style={{
                                            borderRadius: "10px",
                                            padding: 0,
                                            margin: 0,
                                            marginBottom: 10,
                                            color: "#4E657C",
                                            fontSize: 19,
                                            fontWeight: 700,
                                        }}
                                    >
                                        Timezone *
                                    </p>
                                    <Autocomplete
                                        options={timezoneOptions}
                                        value={newWebinar.timezone || null}
                                        onChange={(_, value) => handleChange("timezone", value || "")}
                                        isOptionEqualToValue={(option, value) => option === value}
                                        filterOptions={(options, { inputValue }) => {
                                            const search = inputValue.trim().toLowerCase();
                                            return options.filter((timezone) =>
                                                timezone.toLowerCase().includes(search)
                                            );
                                        }}
                                        noOptionsText="No timezones found"
                                        sx={{ width: "100%", minWidth: 0 }}
                                        slotProps={{
                                            paper: { sx: { overflow: "hidden" } },
                                            listbox: { sx: { maxHeight: 240, overflowY: "auto" } },
                                            popper: {
                                                placement: "bottom-start",
                                                modifiers: [
                                                    { name: "flip", enabled: false },
                                                ],
                                            },
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="Select timezone" error={Boolean(errors?.timezone)} />
                                        )}
                                    />
                                    {errors?.timezone && (
                                        <p className="input-error-text">{errors.timezone}</p>
                                    )}
                                </Box>   
                                </Box>          
                    </Box>
                </DialogContent>
                <DialogActions sx={{ mr: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <TextButtonComponet text={"Update"} width={120} onButtonClick={handleOpenPreview}/>
                        <TextButtonComponet text={"Cancel"} classStyle="btn btn-secondary" width={120} onButtonClick={() => { onClose(); emptyFormfields()  }} />
                    </Box>

                </DialogActions>
            </form>

        {/* Render the preview modal with webinarData prop */}
        {isPreviewOpen && (
          <WebinarPreviewModal
            open={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            onConfirm={handleConfirmUpdate}
            webinarData={stagedPayload}
            mode="edit"
          />
          )}
        </>
    );
}

export default connect(
    state => ({
        courseList: state.students.get("commonCourseList"),
    }),
    {
        getCourseList: Actions.students.getCourseList,
    },
)(AdminWebinarUpdateForm);
